import type { CompanySlug, ModelView } from "../shared/contracts.ts";

export type LandscapeColoringMode = "uniform" | "name";

export interface ModelColorFamily {
  id: string;
  label: string;
  color: string;
}

export interface ModelColorGroup {
  id: string;
  label: string;
  familyIds: ReadonlyArray<string>;
}

export interface ModelColorScheme {
  companySlug: "openai" | "anthropic";
  families: ReadonlyArray<ModelColorFamily>;
  groups?: ReadonlyArray<ModelColorGroup>;
}

interface InternalModelColorScheme extends ModelColorScheme {
  classify: (modelId: string) => string;
}

const ANTHROPIC_FAMILIES: ReadonlyArray<ModelColorFamily> = [
  { id: "claude-2", label: "Claude 2", color: "#aeb5b7" },
  { id: "haiku", label: "Haiku", color: "#4d8dff" },
  { id: "sonnet", label: "Sonnet", color: "#39e76f" },
  { id: "opus", label: "Opus", color: "#e7a56d" },
  { id: "fable", label: "Fable", color: "#d98bf7" },
  { id: "other-claude", label: "Other Claude", color: "#c7b8a3" },
];

const OPENAI_FAMILIES: ReadonlyArray<ModelColorFamily> = [
  { id: "gpt-oss", label: "gpt-oss", color: "#cbd5e1" },
  { id: "gpt-nano", label: "GPT nano", color: "#8b98a5" },
  { id: "o-mini", label: "o-mini", color: "#6478ff" },
  { id: "gpt-mini", label: "GPT mini", color: "#43b7e8" },
  { id: "luna", label: "Luna", color: "#93a7ff" },
  { id: "gpt", label: "GPT", color: "#65d6ad" },
  { id: "terra", label: "Terra", color: "#9bd866" },
  { id: "o-series", label: "o-series", color: "#f28cb1" },
  { id: "sol", label: "Sol", color: "#ff5ea8" },
];

const OPENAI_GROUPS: ReadonlyArray<ModelColorGroup> = [
  { id: "lean", label: "Lean", familyIds: ["gpt-oss", "gpt-nano"] },
  { id: "efficient", label: "Efficient", familyIds: ["o-mini", "gpt-mini", "luna"] },
  { id: "mainline", label: "Mainline", familyIds: ["gpt", "terra"] },
  { id: "frontier", label: "Frontier", familyIds: ["o-series", "sol"] },
];

const SCHEMES: Record<"openai" | "anthropic", InternalModelColorScheme> = {
  anthropic: {
    companySlug: "anthropic",
    families: ANTHROPIC_FAMILIES,
    classify: (modelId) => {
      const id = modelId.toLowerCase();
      if (/^anthropic\/claude-2(?:\.|-|$)/.test(id)) return "claude-2";
      if (id.includes("haiku")) return "haiku";
      if (id.includes("sonnet")) return "sonnet";
      if (id.includes("opus")) return "opus";
      if (id.includes("fable")) return "fable";
      return "other-claude";
    },
  },
  openai: {
    companySlug: "openai",
    families: OPENAI_FAMILIES,
    groups: OPENAI_GROUPS,
    classify: (modelId) => {
      const id = modelId.toLowerCase();
      if (/(?:^|[-/])sol(?:-|$)/.test(id)) return "sol";
      if (/(?:^|[-/])terra(?:-|$)/.test(id)) return "terra";
      if (/(?:^|[-/])luna(?:-|$)/.test(id)) return "luna";
      if (id.includes("gpt-oss")) return "gpt-oss";
      if (/^openai\/o\d+(?:\.\d+)?-mini(?:-|$)/.test(id)) return "o-mini";
      if (/^openai\/o\d+(?:\.\d+)?(?:-|$)/.test(id)) return "o-series";
      if (id.includes("-nano")) return "gpt-nano";
      if (id.includes("-mini")) return "gpt-mini";
      return "gpt";
    },
  },
};

export function modelColorScheme(companySlug: CompanySlug | "all"): ModelColorScheme | null {
  return companySlug === "openai" || companySlug === "anthropic" ? SCHEMES[companySlug] : null;
}

export function modelColorFamily(model: Pick<ModelView, "companySlug" | "id">): ModelColorFamily | null {
  if (model.companySlug !== "openai" && model.companySlug !== "anthropic") return null;
  const scheme = SCHEMES[model.companySlug];
  const familyId = scheme.classify(model.id);
  return scheme.families.find((family) => family.id === familyId) ?? null;
}
